<template>
    <div class="max-w-8xl mx-auto w-full space-y-4">
        <!-- Page Header -->
        <div class="flex flex-wrap items-center justify-between gap-3">
            <h1 class="text-xl font-semibold text-gray-900 dark:text-white">
                Subjects Management
            </h1>
            <button @click="openModal" class="btn btn-primary">
                <Plus class="w-4 h-4 mr-1" />
                Add Subject
            </button>
        </div>

        <!-- Search and Filters -->
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div class="space-y-3">
                <!-- Search -->
                <input
                    v-model="searchQuery"
                    @input="debouncedSearch"
                    type="text"
                    placeholder="Search by name or code..."
                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />

                <div class="flex flex-wrap gap-3">
                    <!-- Grade Level Filter -->
                    <SearchableSelect
                        v-model="filters.grade_level"
                        :options="gradeLevelOptions"
                        placeholder="All Levels"
                        option-label="label"
                        option-value="value"
                        class="flex-1 min-w-[140px]"
                        @update:model-value="fetchSubjects"
                    />

                    <!-- Major Filter -->
                    <SearchableSelect
                        v-model="filters.major_id"
                        :options="majorOptions"
                        placeholder="All Majors"
                        option-label="name"
                        option-value="id"
                        class="flex-1 min-w-[140px]"
                        @update:model-value="fetchSubjects"
                    />

                    <!-- Status Filter -->
                    <SearchableSelect
                        v-model="filters.is_active"
                        :options="statusOptions"
                        placeholder="All Status"
                        option-label="label"
                        option-value="value"
                        class="flex-1 min-w-[140px]"
                        @update:model-value="fetchSubjects"
                    />

                    <button
                        @click="resetFilters"
                        v-if="hasActiveFilters"
                        class="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                    >
                        <RotateCcw class="w-4 h-4" />
                        Reset Filters
                    </button>
                </div>
            </div>
        </div>

        <!-- Loading State -->
        <div
            v-if="subjectStore.loading"
            class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden"
        >
            <SkeletonLoader variant="table" :rows="6" :cols="7" />
        </div>

        <!-- Error State -->
        <div
            v-if="subjectStore.error"
            class="bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg p-4 mb-6"
        >
            <div class="flex">
                <div class="flex-shrink-0">
                    <div class="h-5 w-5 text-danger-400">⚠️</div>
                </div>
                <div class="ml-3">
                    <h3
                        class="text-sm font-medium text-danger-800 dark:text-danger-200"
                    >
                        Error
                    </h3>
                    <p
                        class="mt-1 text-sm text-danger-700 dark:text-danger-300"
                    >
                        {{ subjectStore.error }}
                    </p>
                    <button
                        @click="subjectStore.clearError"
                        class="mt-2 text-sm text-danger-600 dark:text-danger-400 hover:text-danger-500"
                    >
                        Dismiss
                    </button>
                </div>
            </div>
        </div>

        <!-- Data Table -->
        <div
            v-if="!subjectStore.loading && !subjectStore.error"
            class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden"
        >
            <div class="overflow-x-auto">
                <table
                    class="min-w-full divide-y divide-gray-200 dark:divide-gray-700"
                    style="width: 100%"
                >
                    <thead class="bg-gray-50 dark:bg-gray-700">
                        <tr>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Code
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Name
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Grade Level
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Major
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Credits
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Status
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody
                        class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700"
                    >
                        <tr
                            v-for="subject in subjectStore.subjects"
                            :key="subject.id"
                            class="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        >
                            <td class="px-6 py-4 whitespace-nowrap">
                                <span
                                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200"
                                >
                                    {{ subject.code }}
                                </span>
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white"
                            >
                                {{ subject.name }}
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400"
                            >
                                {{ formatGradeLevel(subject.grade_level) }}
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400"
                            >
                                {{ subject.major?.name || "-" }}
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400"
                            >
                                {{ subject.credit_hours }}
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap">
                                <span
                                    v-if="subject.is_active"
                                    class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-200"
                                >
                                    Active
                                </span>
                                <span
                                    v-else
                                    class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-danger-100 text-danger-800 dark:bg-danger-900 dark:text-danger-200"
                                >
                                    Inactive
                                </span>
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm font-medium"
                            >
                                <button
                                    @click="editSubject(subject)"
                                    title="Edit Subject"
                                    class="inline-flex items-center justify-center p-2 text-primary-600 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-300 rounded-md hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors mr-2"
                                >
                                    <Pencil class="w-4 h-4" />
                                </button>
                                <button
                                    @click="confirmDelete(subject)"
                                    title="Delete Subject"
                                    class="inline-flex items-center justify-center p-2 text-danger-600 dark:text-danger-400 hover:text-danger-900 dark:hover:text-danger-300 rounded-md hover:bg-danger-50 dark:hover:bg-danger-900/20 transition-colors"
                                >
                                    <Trash2 class="w-4 h-4" />
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Empty State -->
            <EmptyState
                v-if="subjectStore.subjects.length === 0"
                :icon="BookOpen"
                title="No subjects found"
                description="Get started by adding a new subject."
            >
                <button @click="openModal" class="btn btn-primary">
                    <Plus class="w-4 h-4 mr-1" />
                    Add Subject
                </button>
            </EmptyState>

            <!-- Pagination -->
            <BasePagination
                :pagination="subjectStore.meta"
                :per-page="subjectStore.perPage"
                @page-change="goToPage"
                @per-page-change="handlePerPageChange"
            />
        </div>

        <!-- Subject Modal -->
        <SubjectModal
            v-if="showModal"
            :show-modal="showModal"
            :subject="selectedSubject"
            :is-editing="isEditing"
            @close="closeModal"
            @save="handleSave"
            @saved="handleSaved"
        />

        <!-- Delete Confirmation Modal -->
        <DeleteConfirmationModal
            v-if="showDeleteModal"
            :show="showDeleteModal"
            :title="deleteModalTitle"
            :message="deleteModalMessage"
            @confirm="handleDelete"
            @close="closeDeleteModal"
        />
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useSubjectStore } from "@/stores/subjectStore";
import { useMajorStore } from "@/stores/majorStore";
import SearchableSelect from "@/components/SearchableSelect.vue";
import SubjectModal from "@/modules/subjects/components/SubjectModal.vue";
import DeleteConfirmationModal from "@/components/DeleteConfirmationModal.vue";
import BasePagination from "@/components/BasePagination.vue";
import SkeletonLoader from "@/components/SkeletonLoader.vue";
import EmptyState from "@/components/EmptyState.vue";
import NotificationService from "@/utils/notifications";
import majorApi from "@/api/major";
import type { Subject } from "@/api/subject";
import type { Major } from "@/api/major";
import { Plus, RotateCcw, Pencil, Trash2, BookOpen } from "@lucide/vue";

const subjectStore = useSubjectStore();
const majorStore = useMajorStore();
// State
const searchQuery = ref("");
const filters = ref({
    grade_level: "",
    major_id: "",
    is_active: null as boolean | null,
});
const showModal = ref(false);
const showDeleteModal = ref(false);
const selectedSubject = ref<Subject | null>(null);
const isEditing = ref(false);
const subjectToDelete = ref<Subject | null>(null);

// Delete modal properties
const deleteModalTitle = ref("Confirm Delete");
const deleteModalMessage = ref(
    "Are you sure you want to delete this subject? This action cannot be undone.",
);

// Computed
const hasActiveFilters = computed(() => {
    return (
        searchQuery.value.trim() !== "" ||
        filters.value.grade_level !== "" ||
        filters.value.major_id !== "" ||
        filters.value.is_active !== null ||
        subjectStore.page !== 1 ||
        subjectStore.perPage !== 10
    );
});

// Options for Grade Level filter
const gradeLevelOptions = computed(() => [
    { value: "", label: "All Levels" },
    { value: "X", label: "Grade X" },
    { value: "XI", label: "Grade XI" },
    { value: "XII", label: "Grade XII" },
    { value: "ALL", label: "All Grades" },
]);

// Options for Status filter
const statusOptions = [
    { value: null, label: "All Status" },
    { value: true, label: "Active" },
    { value: false, label: "Inactive" },
];

// Options for Major filter
const majorOptions = computed(() => {
    const options = [{ id: "", name: "All Majors" }];
    majorStore.activeMajors.forEach((major: any) => {
        options.push({ id: major.id, name: major.name });
    });
    return options;
});

// Methods
const handlePerPageChange = (value: number) => {
    subjectStore.perPage = value;
    subjectStore.page = 1;
    fetchSubjects();
};

const resetFilters = () => {
    searchQuery.value = "";
    filters.value = {
        grade_level: "",
        major_id: "",
        is_active: null,
    };
    subjectStore.page = 1;
    subjectStore.perPage = 10;
    fetchSubjects();
};

const fetchSubjects = async () => {
    const searchFilters = {
        ...filters.value,
        search: searchQuery.value,
    };
    await subjectStore.fetchSubjects(searchFilters);
};

const debouncedSearch = useDebounce(fetchSubjects, 300);

const openModal = () => {
    selectedSubject.value = null;
    isEditing.value = false;
    showModal.value = true;
};

const editSubject = (subject: Subject) => {
    selectedSubject.value = subject;
    isEditing.value = true;
    showModal.value = true;
};

const closeModal = () => {
    showModal.value = false;
    selectedSubject.value = null;
    isEditing.value = false;
};

const confirmDelete = (subject: Subject) => {
    subjectToDelete.value = subject;
    deleteModalTitle.value = "Delete Subject";
    deleteModalMessage.value = `Are you sure you want to delete ${subject.name}? This action cannot be undone.`;
    showDeleteModal.value = true;
};

const closeDeleteModal = () => {
    showDeleteModal.value = false;
    subjectToDelete.value = null;
};

const handleSave = async (subjectData: any) => {
    // This function is now handled within the modal
    // Validation errors are handled in modal and don't reach here
    // Only successful operations will trigger to 'saved' event
};

const handleSaved = () => {
    // Refresh the subjects list to show new/updated subject
    fetchSubjects();
    showNotification(
        isEditing.value
            ? "Subject updated successfully"
            : "Subject created successfully",
        "success",
    );
    closeModal();
};

const handleDelete = async () => {
    try {
        if (subjectToDelete.value) {
            await subjectStore.deleteSubject(subjectToDelete.value.id);
            showNotification("Subject deleted successfully", "success");
            closeDeleteModal();
        }
    } catch (error) {
        showNotification("Failed to delete subject", "error");
    }
};

const goToPage = (page: number) => {
    subjectStore.setPage(page);
    fetchSubjects();
};

const formatGradeLevel = (level: string) => {
    switch (level) {
        case "X":
            return "Grade X";
        case "XI":
            return "Grade XI";
        case "XII":
            return "Grade XII";
        case "ALL":
            return "All Grades";
        default:
            return level || "-";
    }
};

const showNotification = (message: string, type: "success" | "error") => {
    if (type === "success") {
        NotificationService.success(message);
    } else {
        NotificationService.error(message);
    }
};

// Debounce utility
function useDebounce(fn: Function, delay: number) {
    let timeoutId: ReturnType<typeof setTimeout>;
    return (...args: any[]) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn(...args), delay);
    };
}

// Lifecycle
onMounted(() => {
    fetchSubjects();
    majorStore.fetchActiveMajors();
});
</script>
